import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Search, ExternalLink, Download, ArrowUp, ArrowDown, ArrowUpDown, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import {
  fetchHelloAssoOrders,
  syncHelloAssoOrders,
  syncHelloAssoPayouts,
  type HelloAssoOrderListItem,
} from "@/lib/helloasso-sync";
import { OrderImportDialog } from "@/components/OrderImportDialog";
import { OrderDetailDialog } from "@/components/OrderDetailDialog";

export const Route = createFileRoute("/orders")({
  component: () => (
    <Guard module="treasury">
      <OrdersPage />
    </Guard>
  ),
});

const fmtEUR = (n: number | string | null | undefined) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n ?? 0));

const fmtDate = (s: string | null) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR");
};

const FORM_TYPE_LABELS: Record<string, string> = {
  Membership: "Adhésion",
  Event: "Événement",
  Donation: "Don",
  Shop: "Boutique",
  PaymentForm: "Formulaire de paiement",
  CrowdFunding: "Financement participatif",
  Checkout: "Paiement",
};
const translateFormType = (t: string | null | undefined) =>
  t ? (FORM_TYPE_LABELS[t] ?? t) : "—";

export function cashOutBadge(state: string | null) {
  if (!state) return <Badge variant="outline">—</Badge>;
  const map: Record<string, { className: string; label: string }> = {
    Transfered:                     { label: "Payé",                  className: "bg-green-600 text-white hover:bg-green-600/90 border-transparent" },
    WaitingForCashOutConfirmation:  { label: "Payé",                  className: "bg-green-600 text-white hover:bg-green-600/90 border-transparent" },
    CashedOut:                      { label: "Payé",                  className: "bg-green-600 text-white hover:bg-green-600/90 border-transparent" },
    MoneyIn:                        { label: "Paiement en cours",     className: "bg-amber-500 text-white hover:bg-amber-500/90 border-transparent" },
    TransferInProgress:             { label: "Paiement en cours",     className: "bg-amber-500 text-white hover:bg-amber-500/90 border-transparent" },
    Refunding:                      { label: "Remboursement en cours", className: "bg-red-600 text-white hover:bg-red-600/90 border-transparent" },
    Refunded:                       { label: "Remboursé",             className: "bg-white text-foreground border border-border" },
  };
  const cfg = map[state];
  if (!cfg) return <Badge variant="outline">{state}</Badge>;
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

type SortKey =
  | "order_date" | "payer" | "form_type" | "form_name" | "form_slug"
  | "items_count" | "amount_vat" | "amount_discount" | "amount_total"
  | "cash_out_state";
type SortDir = "asc" | "desc";

const ALL = "__all__";

// Groupes de statuts de paiement (alignés sur le badge de la colonne Paiement)
const PAYMENT_STATUS_GROUPS: Record<string, { label: string; states: string[] }> = {
  paid:       { label: "Payé",                   states: ["Transfered", "WaitingForCashOutConfirmation", "CashedOut"] },
  in_progress:{ label: "Paiement en cours",      states: ["MoneyIn", "TransferInProgress"] },
  refunding:  { label: "Remboursement en cours", states: ["Refunding"] },
  refunded:   { label: "Remboursé",              states: ["Refunded"] },
};

function OrdersPage() {
  const qc = useQueryClient();
  const { hasAccess } = useAuth();
  const canSync = hasAccess("treasury", "admin");
  const canImport = hasAccess("treasury", "write");

  const [search, setSearch] = useState("");
  const [formSlugFilter, setFormSlugFilter] = useState<string>(ALL);
  const [formTypeFilter, setFormTypeFilter] = useState<string>(ALL);
  const [cashOutFilter, setCashOutFilter] = useState<string>(ALL);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [sortKey, setSortKey] = useState<SortKey>("order_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [importingOrderId, setImportingOrderId] = useState<string | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["helloasso-orders"],
    queryFn: () => fetchHelloAssoOrders({ limit: 1000 }),
  });

  const syncMut = useMutation({
    mutationFn: async () => {
      const ordersRes = await syncHelloAssoOrders();
      let payoutsMessage = "";
      try {
        const payoutsRes = await syncHelloAssoPayouts();
        payoutsMessage = payoutsRes?.message ? " · " + payoutsRes.message : "";
      } catch (e) {
        payoutsMessage = " · Versements : " + (e as Error).message;
      }
      return {
        message: (ordersRes?.message ?? "Commandes synchronisées") + payoutsMessage,
      };
    },
    onSuccess: (res) => {
      toast.success(res.message ?? "Synchronisation terminée");
      qc.invalidateQueries({ queryKey: ["helloasso-orders"] });
    },
    onError: (e: Error) => toast.error("Échec de la synchronisation : " + e.message),
  });

  // Listes uniques pour les selects
  const formSlugs = useMemo(() => {
    const s = new Set<string>();
    orders.forEach((o) => { if (o.form_slug) s.add(o.form_slug); });
    return Array.from(s).sort();
  }, [orders]);

  const formTypes = useMemo(() => {
    const s = new Set<string>();
    orders.forEach((o) => { if (o.form_type) s.add(o.form_type); });
    return Array.from(s).sort();
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTs = fromDate ? new Date(fromDate + "T00:00:00").getTime() : null;
    const toTs   = toDate   ? new Date(toDate   + "T23:59:59").getTime() : null;

    return orders.filter((o) => {
      if (q) {
        const hay = [
          o.payer_first_name, o.payer_last_name, o.payer_email,
          o.form_name, o.form_slug, o.external_id,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (formSlugFilter !== ALL && o.form_slug !== formSlugFilter) return false;
      if (formTypeFilter !== ALL && o.form_type !== formTypeFilter) return false;
      if (cashOutFilter !== ALL) {
        if (cashOutFilter === "__none__") {
          if (o.cash_out_state) return false;
        } else {
          const group = PAYMENT_STATUS_GROUPS[cashOutFilter];
          if (!group || !o.cash_out_state || !group.states.includes(o.cash_out_state)) return false;
        }
      }
      if (fromTs || toTs) {
        const t = o.order_date ? new Date(o.order_date).getTime() : NaN;
        if (Number.isNaN(t)) return false;
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }
      return true;
    });
  }, [orders, search, formSlugFilter, formTypeFilter, cashOutFilter, fromDate, toDate]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (o: HelloAssoOrderListItem): string | number => {
      switch (sortKey) {
        case "order_date":     return o.order_date ? new Date(o.order_date).getTime() : 0;
        case "payer":          return [o.payer_last_name, o.payer_first_name].filter(Boolean).join(" ").toLowerCase();
        case "form_type":      return translateFormType(o.form_type).toLowerCase();
        case "form_name":      return (o.form_name ?? "").toLowerCase();
        case "form_slug":      return (o.form_slug ?? "").toLowerCase();
        case "items_count":    return Number(o.items_count ?? 0);
        case "amount_vat":     return Number(o.amount_vat ?? 0);
        case "amount_discount":return Number(o.amount_discount ?? 0);
        case "amount_total":   return Number(o.amount_total ?? 0);
        case "cash_out_state": return (o.cash_out_state ?? "").toLowerCase();
      }
    };
    arr.sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalAmount = useMemo(
    () => sorted.reduce((sum, o) => sum + Number(o.amount_total ?? 0), 0),
    [sorted],
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "order_date" || key.startsWith("amount") ? "desc" : "asc"); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-50" />;
    return sortDir === "asc"
      ? <ArrowUp className="ml-1 inline h-3 w-3" />
      : <ArrowDown className="ml-1 inline h-3 w-3" />;
  };

  const SortableHead = ({ col, label, className }: { col: SortKey; label: string; className?: string }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(col)}
        className="inline-flex items-center font-medium hover:text-foreground transition-colors"
      >
        {label}<SortIcon col={col} />
      </button>
    </TableHead>
  );

  const resetFilters = () => {
    setSearch(""); setFormSlugFilter(ALL); setFormTypeFilter(ALL); setCashOutFilter(ALL);
    setFromDate(""); setToDate("");
  };
  const hasActiveFilter =
    search !== "" || formSlugFilter !== ALL || formTypeFilter !== ALL || cashOutFilter !== ALL || fromDate !== "" || toDate !== "";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Commandes HelloAsso"
        description="Détail des commandes (orders) passées sur vos formulaires HelloAsso"
        actions={
          canSync ? (
            <Button onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
              <RefreshCw className={"mr-2 h-4 w-4 " + (syncMut.isPending ? "animate-spin" : "")} />
              {syncMut.isPending ? "Synchronisation…" : "Synchroniser"}
            </Button>
          ) : null
        }
      />

      <Card className="p-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher (nom, email, formulaire, slug…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select value={formTypeFilter} onValueChange={setFormTypeFilter}>
            <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les types</SelectItem>
              {formTypes.map((t) => (
                <SelectItem key={t} value={t}>{translateFormType(t)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={formSlugFilter} onValueChange={setFormSlugFilter}>
            <SelectTrigger><SelectValue placeholder="Formulaire" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les formulaires</SelectItem>
              {formSlugs.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={cashOutFilter} onValueChange={setCashOutFilter}>
            <SelectTrigger><SelectValue placeholder="Paiement" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous statuts</SelectItem>
              {Object.entries(PAYMENT_STATUS_GROUPS).map(([key, group]) => (
                <SelectItem key={key} value={key}>{group.label}</SelectItem>
              ))}
              <SelectItem value="__none__">— (non renseigné)</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex gap-2">
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              placeholder="Du"
              title="Date début"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              placeholder="Au"
              title="Date fin"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {sorted.length} commande{sorted.length > 1 ? "s" : ""} · Total{" "}
            <span className="font-semibold text-foreground">{fmtEUR(totalAmount)}</span>
          </div>
          {hasActiveFilter && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="mr-1 h-4 w-4" /> Réinitialiser les filtres
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead col="order_date" label="Date" />
              <SortableHead col="payer" label="Payeur" />
              <SortableHead col="form_type" label="Type" />
              <SortableHead col="form_slug" label="Formulaire" />
              <SortableHead col="items_count" label="Items" className="text-center" />
              <SortableHead col="amount_vat" label="TVA" className="text-right" />
              <SortableHead col="amount_discount" label="Remise" className="text-right" />
              <SortableHead col="amount_total" label="Total" className="text-right" />
              <SortableHead col="cash_out_state" label="Paiement" />
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  Aucune commande. {canSync && "Cliquez sur « Synchroniser » pour importer depuis HelloAsso."}
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((o: HelloAssoOrderListItem) => (
                <TableRow key={o.id} className="hover:bg-muted/40">
                  <TableCell className="whitespace-nowrap">{fmtDate(o.order_date)}</TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {[o.payer_first_name, o.payer_last_name].filter(Boolean).join(" ") || "—"}
                    </div>
                    {o.payer_email && (
                      <div className="text-xs text-muted-foreground">{o.payer_email}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{translateFormType(o.form_type)}</div>
                    {o.form_name && (
                      <div className="text-xs text-muted-foreground">{o.form_name}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <code className="text-xs text-muted-foreground">{o.form_slug || "—"}</code>
                  </TableCell>
                  <TableCell className="text-center">{o.items_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtEUR(o.amount_vat)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtEUR(o.amount_discount)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {fmtEUR(o.amount_total)}
                  </TableCell>
                  <TableCell>{cashOutBadge(o.cash_out_state)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {canImport && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Importer les items en trésorerie"
                          onClick={() => setImportingOrderId(o.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Voir le détail"
                        onClick={() => setDetailOrderId(o.id)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <OrderImportDialog
        orderId={importingOrderId}
        onClose={() => {
          setImportingOrderId(null);
          qc.invalidateQueries({ queryKey: ["helloasso-orders"] });
        }}
      />

      <OrderDetailDialog
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}
